/**
 * TextBlock component
 *
 * Toggle expandir/colapsar del bloque `text_block` en `hg/page-section`.
 * Markup: `.hg-text-block` → body-visible, `.hg-text-block__readmore-panel` →
 * panel-inner (`inert` + `aria-hidden` colapsado) → `.hg-text-block__body-hidden`,
 * `.hg-text-block__trigger` → botón.
 *
 * Animación de altura vía `grid-template-rows: 0fr` ↔ `1fr` y clase `.is-open`
 * en el wrapper. Al cerrar, `inert` y `aria-hidden` se restauran en `transitionend`.
 */

const FALLBACK_LABEL_EXPAND = 'Sigue leyendo'
const FALLBACK_LABEL_COLLAPSE = 'Mostrar menos'

export default class TextBlock {
	private wrapper: HTMLElement
	private toggle: HTMLButtonElement | null = null
	private bodyHidden: HTMLElement | null = null
	private panel: HTMLElement | null = null
	private panelInner: HTMLElement | null = null
	private labelExpand: string = FALLBACK_LABEL_EXPAND
	private labelCollapse: string = FALLBACK_LABEL_COLLAPSE
	private bound = false
	private onPanelTransitionEndBound = this.onPanelTransitionEnd.bind(this)

	constructor(wrapper: HTMLElement) {
		this.wrapper = wrapper
	}

	init() {
		this.toggle = this.wrapper.querySelector<HTMLButtonElement>('.hg-text-block__toggle')
		this.bodyHidden = this.wrapper.querySelector<HTMLElement>('.hg-text-block__body-hidden')
		this.panel = this.wrapper.querySelector<HTMLElement>('.hg-text-block__readmore-panel')
		this.panelInner = this.wrapper.querySelector<HTMLElement>('.hg-text-block__readmore-panel-inner')

		if (!this.toggle || !this.bodyHidden || !this.panel || !this.panelInner) return

		const expand = this.toggle.dataset.labelExpand
		const collapse = this.toggle.dataset.labelCollapse
		if (expand && expand.length > 0) this.labelExpand = expand
		if (collapse && collapse.length > 0) this.labelCollapse = collapse

		this.toggle.addEventListener('click', this.onToggleClick)
		this.panel.addEventListener('transitionend', this.onPanelTransitionEndBound)
		this.bound = true
	}

	cleanup() {
		if (this.bound) {
			this.toggle?.removeEventListener('click', this.onToggleClick)
			this.panel?.removeEventListener('transitionend', this.onPanelTransitionEndBound)
		}
		this.bound = false
	}

	private onToggleClick = () => {
		if (!this.toggle || !this.bodyHidden || !this.panelInner) return

		const isExpanded = this.toggle.getAttribute('aria-expanded') === 'true'

		if (isExpanded) {
			this.collapse()
		} else {
			this.expand()
		}
	}

	private expand() {
		if (!this.toggle || !this.panelInner) return

		this.panelInner.inert = false
		this.panelInner.setAttribute('aria-hidden', 'false')
		this.wrapper.classList.add('is-open')
		this.toggle.setAttribute('aria-expanded', 'true')
		this.toggle.textContent = this.labelCollapse

		requestAnimationFrame(() => this.notifyScrollRefresh())
	}

	private collapse() {
		if (!this.toggle || !this.panelInner) return

		this.wrapper.classList.remove('is-open')
		this.toggle.setAttribute('aria-expanded', 'false')
		this.toggle.textContent = this.labelExpand
	}

	private onPanelTransitionEnd(e: TransitionEvent) {
		if (e.target !== this.panel) return
		if (e.propertyName !== 'grid-template-rows') return
		if (this.wrapper.classList.contains('is-open')) {
			this.notifyScrollRefresh()
			return
		}
		if (!this.panelInner) return
		this.panelInner.inert = true
		this.panelInner.setAttribute('aria-hidden', 'true')
		this.notifyScrollRefresh()
	}

	private notifyScrollRefresh = () => {
		document.dispatchEvent(new CustomEvent('hg:refresh-scroll'))
	}
}
